<script setup lang="ts">
import PlusImgUrl from '@/assets/components/loggedBox/plus.webp?url'

const props = defineProps<{
  offer: Games.Offer
  mini?: boolean
  medium?: boolean
  big?: boolean
  showAllItems?: boolean
  textClass?: string
  visibleLength?: number
}>()
const { games, numbersShortHuman } = siteUtils
const { t } = useI18n()

const gameSettings = computed(() => {
  return games?.[props.offer?.gameKey || '']
})

const items = computed<Games.OfferItemDescription[]>(() => {
  return [...(props.offer.items || [])].filter(item => !!item.amount)
})
const bonusItems = computed<Games.OfferItemDescription[]>(() => {
  const bonusItems: Games.OfferItemDescription[] = []
  if (props.offer?.loyaltyPoints) {
    bonusItems.push({
      bonusAmount: props.offer.loyaltyPoints,
      _bonusAmountShort: numbersShortHuman(props.offer.loyaltyPoints || 0),
      title: 'coin',
      label: t('offerTile.loyaltyPoints'),
      asset: gameSettings.value?.loyalty?.icon || ''
    })
  }

  const bonusItem = (props.offer?.items || []).find((item) => {
    return !!item.bonusAmount
  })
  if (bonusItem) {
    bonusItems.push(bonusItem)
  }
  return bonusItems
})
const { windowWidth } = tools
const visibleItems = computed(() => {
  if (props.showAllItems) {
    return items.value
  }
  let length = props?.visibleLength ?? 4
  if (props?.visibleLength === undefined) {
    if (windowWidth.value < 360) {
      length = 3
    } else if (windowWidth.value <= 550) {
      length = 4
    } else if (windowWidth.value > 550 && windowWidth.value < 900) {
      length = 6
    } else if (windowWidth.value >= 900 && windowWidth.value < 1200) {
      length = 4
    } else {
      length = 5
    }
  }
  length += 2 - bonusItems.value.length
  return items.value.slice(0, items.value.length > length ? length - 1 : items.value.length)
})
</script>

<template>
  <div
    v-if="items.length || bonusItems.length"
    class="absolute bottom-0 left-0 right-0 flex flex-row flex-wrap content-center justify-center leading-none gap-1 mini:gap-2.5 md:gap-2 big:gap-1 lg:gap-2 translate-y-6"
    :class="{
      mini,
      medium,
      big
    }"
  >
    <!-- Items bonusAmount -->
    <template
      v-for="(bonusItem, itemIndex) in bonusItems"
      :key="`bonus_${offer.id}_${itemIndex}`"
    >
      <el-tooltip
        placement="top"
        :show-after="600"
        effect="light"
        :disabled="!(bonusItem?.label || bonusItem?.title)?.length && !bonusItem?.description?.length"
        raw-content
      >
        <template #content>
          <div class="max-w-80">
            <div>{{ bonusItem?.label || bonusItem?.title }}</div>
            <div v-if="bonusItem?.description?.length && (bonusItem?.label || bonusItem?.title) !== bonusItem?.description && (bonusItem?.label || bonusItem?.title) !== `${bonusItem?.description}s`">
              {{ bonusItem?.description }}
            </div>
          </div>
        </template>
        <div class="item-box bg-white w-11 h-11 mini:w-12 mini:h-12 rounded-lg border-2 border-[#3da5bb] shadow-xs relative">
          <div class="absolute left-1.5 right-1.5 bottom-1.5 top-1">
            <template v-if="bonusItem?.asset">
              <img
                alt="item"
                :src="bonusItem.asset"
                class="block max-w-9 mini:max-w-11 big:max-w-9 mx-auto"
                :class="[games?.[offer?.gameKey]?.itemIconClass]"
              >
            </template>
          </div>
          <div
            v-if="bonusItem._bonusAmountShort"
            class="text-center font-semibold leading-none p-0.5 whitespace-nowrap z-10 text-[0.8rem] mini:text-[0.95rem] big:text-[0.8rem] lg:text-[0.9rem]"
          >
            <div
              class="gs-text-shadow font-black text-white text-center absolute left-1/2 -translate-x-1/2 bottom-0.5"
              :class="{ 'tracking-tighter': bonusItem._bonusAmountShort.length > 4 }"
            >
              {{ bonusItem._bonusAmountShort }}
            </div>
          </div>
        </div>
      </el-tooltip>
    </template>
    <!-- Plus -->
    <template v-if="bonusItems.length && items.length">
      <div class="item-box bg-transparent h-11 mini:h-12">
        <div class="h-full text-center flex items-center justify-center">
          <img
            alt="plus"
            class="w-6 mini:w-8"
            :src="PlusImgUrl"
          >
        </div>
      </div>
    </template>
    <!-- Items: amount -->
    <template
      v-for="(item, itemIndex) in visibleItems"
      :key="`${offer.id}_${itemIndex}`"
    >
      <el-tooltip
        placement="top"
        :show-after="600"
        effect="light"
        :disabled="!(item.label || item?.title)?.length && !item?.description?.length"
        raw-content
      >
        <template #content>
          <div class="max-w-80">
            <div>{{ item.label || item?.title }}</div>
            <div v-if="item?.description?.length && (item.label || item?.title) !== item?.description && (item.label || item?.title) !== `${item?.description}s`">
              {{ item?.description }}
            </div>
          </div>
        </template>
        <div class="item-box bg-white w-11 h-11 mini:w-12 mini:h-12 rounded-lg border border-gray-300 shadow-xs relative">
          <div class="absolute left-1.5 right-1.5 bottom-1.5 top-1">
            <template v-if="item?.asset">
              <img
                alt="item"
                :src="item.asset"
                class="block max-w-9 mini:max-w-11 big:max-w-9 mx-auto"
                :class="[games?.[offer?.gameKey]?.itemIconClass]"
              >
            </template>
          </div>
          <div
            v-if="item._amountShort"
            class="text-center font-semibold leading-none p-0.5 whitespace-nowrap z-10 text-[0.8rem] mini:text-[0.95rem] big:text-[0.8rem] lg:text-[0.9rem]"
          >
            <div
              class="gs-text-shadow font-black text-white text-center absolute left-1/2 -translate-x-1/2 bottom-0.5"
              :class="{ 'tracking-tighter': item._amountShort.length > 4 }"
            >
              {{ item._amountShort }}
            </div>
          </div>
        </div>
      </el-tooltip>
    </template>
    <!-- Item more -->
    <template v-if="items.length > visibleItems.length">
      <div class="item-box bg-white w-11 h-11 mini:w-12 mini:h-12 rounded-lg border border-gray-300 shadow-xs relative">
        <div class="gs-text-shadow font-black text-white h-full text-center">
          <icon-ify
            icon="material-symbols:more-horiz"
            class="text-black text-[1.7rem] inline-block translate-y-1"
          />
          <div
            class="text-center font-semibold leading-none p-0.5 whitespace-nowrap z-10 text-[0.8rem] mini:text-[0.95rem] big:text-[0.8rem] lg:text-0.9rem]"
          >
            <div
              class="gs-text-shadow font-black text-white text-center absolute left-1/2 -translate-x-1/2 bottom-0.5 tracking-tighter"
            >
              {{ t('offerTile.more') }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="postcss">

</style>
